import {Injectable, OnChanges, SimpleChanges} from '@angular/core';
import { Observable, of } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { User } from '@emm/core/models/user.model';
import { Router } from '@angular/router';
import { AuthFacadeService } from '@emm/auth/services/auth-facade.service';

@Injectable({
    providedIn: 'root',
})
export class UserService implements OnChanges {

    private user: User = null;
    private initialized = false;
    private oktaClaims: string[] = [];
    userRoles: string[] = [];
    selectedTestingRoles: string[] = [];
    testingRole$ = this.authFacade.testingRole$;

    constructor(private router: Router, private authFacade: AuthFacadeService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (this.selectedTestingRoles.length > 0) {
            this.userRoles = this.selectedTestingRoles;
        }
    }

    signOut(): void {
        sessionStorage.clear();
        this.user = null;
        this.initialized = false;
        this.userRoles = [];
    }

    getUser(): Observable<User> {
        if (!this.initialized) {
            const localStoreToken = JSON.parse(sessionStorage.getItem('okta-token-storage')).accessToken?.accessToken;
            this.initUser(localStoreToken);

            // TODO: revisit this
            if (this.user.roles === 'Not') {
                return of(null);
            }
        }

        this.initialized = true;
        return of(this.user);
    }

    private initUser(token: string) {
        const jwt = new JwtHelperService();
        const decodedToken = jwt.decodeToken(token);
        try {
            this.user = {
                email: decodedToken.email,
                firstName: decodedToken.nickname,
                isReadOnly: this.isReadOnly(decodedToken.cfa_perms),
                lastName: decodedToken.family_name,
                login: decodedToken.sub,
                roles: this.getUserRolesArray()
            };
        } catch (e) {
            this.user = {
                email: 'lp@lp.com',
                firstName: 'Not',
                isReadOnly: false,
                lastName: 'SignedIn',
                login: 'lp@lp.com',
                roles: 'Not'
            };
        }
    }

    isReadOnly(cfaPerms: any): boolean {
        const claims = cfaPerms['EMM'];
        const login = claims['LOGIN'];
        const keys = Object.keys(claims);
        this.userRoles = keys;

        // return true only if user has login role
        return login && keys.length === 1;
    }

    getUserRoles() {
        const localStoreToken = sessionStorage.getItem('okta-token-storage');
        const tokenAsJson = JSON.parse(localStoreToken);
        const cfaPerms = tokenAsJson['idToken'].claims.cfa_perms.EMM;

        for (const key in cfaPerms) {
            if (cfaPerms.hasOwnProperty(key) && !this.oktaClaims.includes(key)) {
                this.oktaClaims.push(key);
            }
        }
        return this.oktaClaims;
    }

    getUserRolesArray() {
        const claims = this.getUserRoles();
        if (!(this.selectedTestingRoles.length > 0)) {
            this.userRoles = claims;
        } else {
            this.userRoles = this.selectedTestingRoles;
        }
        return this.userRoles.join(', ');
    }

    hasLoginRole(): boolean {
        this.getUserRolesArray();
        return this.userRoles.includes('LOGIN') && this.userRoles.length === 1;
    }

    hasAdminRole(): boolean {
        this.getUserRolesArray();
        return (this.userRoles.includes('ADMIN') && !this.userRoles.includes('SUPER'));
    }

    hasSuperAdminRole(): boolean {
        this.getUserRolesArray();
        return this.userRoles.includes('SUPER_ADMIN');
    }

    hasSuperAdminOrAdminRole() {
        this.getUserRolesArray();
        return this.userRoles.includes('SUPER_ADMIN') || this.userRoles.includes('ADMIN');
    }

    hasAuthorRole(): boolean {
        this.getUserRolesArray();
        return this.userRoles.includes('AUTHOR');
    }

    hasMMSAuthorRole(): boolean {
        this.getUserRolesArray();
        return this.userRoles.includes('MMS_AUTHOR');
    }

    isMmsAuthorOnly(): boolean {
        this.getUserRolesArray();
        return this.userRoles.includes('MMS_AUTHOR') &&
            !this.hasSuperAdminRole() &&
            !this.hasAdminRole() &&
            !this.hasAuthorRole();
    }
}
